import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ 
  onFilesSelected, 
  isUploading = false, 
  acceptedFormats = ['image/jpeg', 'image/png', 'image/webp'],
  maxFileSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 5 
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const validateFile = (file) => {
    if (!acceptedFormats?.includes(file?.type)) {
      return `File type ${file?.type} not supported. Please use JPEG, PNG, or WebP.`;
    }
    if (file?.size > maxFileSize) {
      return `File size too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB.`;
    }
    return null;
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    
    if (fileArray?.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed.`);
      return;
    }

    const validFiles = [];
    const errors = [];

    fileArray?.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors?.push(`${file?.name}: ${validationError}`);
      } else {
        validFiles?.push(file);
      }
    });

    if (errors?.length > 0) {
      setError(errors?.join('\n'));
      return;
    }

    setError('');
    onFilesSelected(validFiles);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    const files = e?.dataTransfer?.files;
    handleFiles(files);
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      handleFiles(files);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef?.current?.click();
  };

  return (
    <div className="w-full">
      {/* Main Upload Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          isDragOver
            ? 'border-accent bg-accent/5 scale-[1.02]'
            : isUploading
            ? 'border-muted bg-muted/20' :'border-border hover:border-accent/50 hover:bg-accent/5'
        } ${isUploading ? 'pointer-events-none' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!isUploading ? handleBrowseClick : undefined}
      >
        {/* Upload Icon and Text */}
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className={`p-4 rounded-full ${
              isUploading ? 'bg-muted' : 'bg-accent/10'
            }`}>
              <Icon 
                name={isUploading ? "Loader2" : "Upload"} 
                size={32} 
                color="var(--color-accent)"
                className={isUploading ? "animate-spin" : ""}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">
              {isUploading ? 'Uploading your bike photos...' : 'Upload Your Bike Photos'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {isUploading 
                ? 'Please wait while we process your images for analysis.'
                : 'Drag and drop your bike photos here, or click to browse. Multiple angles help us provide better recommendations.'
              }
            </p>
          </div>

          {!isUploading && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                variant="default"
                size="sm"
                iconName="FolderOpen"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleBrowseClick();
                }}
              >
                Browse Files
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                iconName="Camera"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleCameraClick();
                }}
                className="sm:inline-flex"
              >
                Take Photo
              </Button>
            </div>
          )}
        </div>

        {/* File Requirements */}
        {!isUploading && (
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="FileImage" size={14} />
                JPEG, PNG, WebP
              </span>
              <span className="flex items-center gap-1">
                <Icon name="HardDrive" size={14} />
                Max {Math.round(maxFileSize / (1024 * 1024))}MB per file
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Images" size={14} />
                Up to {maxFiles} photos
              </span>
            </div>
          </div>
        )}
      </div>
      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
            <div className="text-sm text-error whitespace-pre-line">{error}</div>
          </div>
        </div>
      )}
      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFormats?.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default UploadZone;